import React from 'react'
import PropTypes from 'prop-types'

import {useIntl} from 'react-intl'
import {useProductList} from '../util/product-list-context'
import {
    Box,
    HStack,
    useMultiStyleConfig,
    useRadioGroup,
    useRadio
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ADD_FILTER} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {REFINEMENT_LEGEND} from '../../product-list'
import {REFINEMENT_GENDER_ATTRIBUTE} from '../../../constants'

/**
 * GenderRadio is a functional component that renders a custom radio button
 * using Chakra UI's Box component. It utilizes the `useRadio` hook to manage
 * the radio button's state and properties.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.label - The text label displayed alongside the radio button.
 * @param {Object} [props.restProps] - Additional properties passed to the `useRadio` hook.
 *
 * @returns {JSX.Element} A styled radio button component.
 */
const GenderRadio = ({label, ...restProps}) => {
    const {getInputProps, getRadioProps, getLabelProps} = useRadio(restProps)
    const inputProps = getInputProps()
    const radioProps = getRadioProps()
    const labelProps = getLabelProps()

    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant: 'gender',
        disabled: false
    })

    return (
        <Box as="label" {...labelProps}>
            <input {...inputProps} aria-label={formatMessage(ADD_FILTER, {label})} />
            <Box {...radioProps} {...styles.swatch} aria-hidden={false} cursor="pointer">
                {label}
            </Box>
        </Box>
    )
}

GenderRadio.propTypes = {
    label: PropTypes.string
}

/**
 * GenderFilter is a React functional component that provides a user interface for filtering
 * products based on gender.
 *
 * @returns {JSX.Element|null} A JSX element representing the gender filter UI
 */
const GenderFilter = () => {
    const {formatMessage} = useIntl()
    const {productSearchResult, toggleFilter, searchParams, resetFilters} = useProductList()

    const genderFilter = productSearchResult?.refinements?.find(
        (item) => item.attributeId === REFINEMENT_GENDER_ATTRIBUTE
    )

    let selectedGenderArray = searchParams?.refine?.[REFINEMENT_GENDER_ATTRIBUTE] ?? []

    if (!Array.isArray(selectedGenderArray)) {
        selectedGenderArray = [selectedGenderArray]
    }

    const handleChange = (value) => {
        // run logic only on not checked 'all' radio button
        if (value === 'all' && selectedGenderArray.length) {
            resetFilters(REFINEMENT_GENDER_ATTRIBUTE)
        } else {
            const isSelected = selectedGenderArray.includes(value)

            if (!isSelected) {
                const selectedValue = genderFilter.values.find((v) => v.value === value)
                toggleFilter(selectedValue, REFINEMENT_GENDER_ATTRIBUTE, isSelected, false)
            }
        }
    }

    const {getRootProps, getRadioProps} = useRadioGroup({
        name: 'gender',
        defaultValue: 'all',
        onChange: handleChange
    })

    const group = getRootProps()

    if (!genderFilter?.values?.length) {
        return null
    }

    return (
        <Box as="fieldset" px={[10, 10, 20, 20]} py={20}>
            <legend className="sr-only">
                {formatMessage(REFINEMENT_LEGEND, {label: genderFilter.label})}
            </legend>
            <HStack spacing={{base: 0, lg: '6px'}} {...group}>
                {/* Clear All */}
                <GenderRadio
                    key="all"
                    {...getRadioProps({value: 'all'})}
                    label={formatMessage({id: 'product_list.gender.all', defaultMessage: 'All'})}
                />

                {genderFilter.values.map((value) => (
                    <GenderRadio
                        key={value.value}
                        {...getRadioProps({value: value.value})}
                        label={value.label}
                    />
                ))}
            </HStack>
        </Box>
    )
}

export default GenderFilter
